import { isNil } from '../../../helpers/isNil'
import { UrlSearchParamsMapper, useSearchParams } from '../../../hooks/useSearchParams'
import { LiveEventCalendarAdditionalDataId } from '../types/LiveEventAdditionalCalendarData'
import { PerformanceChangesByOptionValue } from '../types/PerformanceChangesByOption'
import { defaultPerformanceChangesByValue } from './usePerformanceChangesByOptions'
import { SortGamesByOptionValue } from './useSortGamesBySelectOptions'
import { defaultPerformanceEffectThresholdValue } from './useThresholdOptions'

type SearchParamSource = {
  subpage: string | undefined
  gameIds: string | string[]
  liveEventTags: string[]
  liveEventSecondaryTags: string[]
  liveEventDurations: string[]
  liveEventAppearances: string[]
  liveEventPricePoints: string[]
  additionalDatas?: string[] | emptyArray
  sortGamesBy: string
  dateFrom?: string
  dateTo?: string
  performanceEffectThreshold: string
  performanceChangesBy: PerformanceChangesByOptionValue
  feedDateFrom?: string
  feedDateTo?: string
  motivations: string[]
  archetypes: string[]
  groupByBlueprint?: string
  hideDiscontinuedBlueprints?: string
  // Pre-rename names, still read so saved bookmarks and shared links keep their state.
  groupByArchetype?: string
  hideDiscontinuedArchetypes?: string
}

type SearchParamDestination = {
  subpage: string | undefined
  gameIds: string[]
  liveEventTags: string[]
  liveEventSecondaryTags: string[]
  liveEventDurations: string[]
  liveEventAppearances: string[]
  liveEventPricePoints: string[]
  additionalDatas?: string[] | emptyArray
  sortGamesBy: SortGamesByOptionValue
  dateFrom?: number
  dateTo?: number
  performanceEffectThreshold: number
  performanceChangesBy: PerformanceChangesByOptionValue
  feedDateFrom?: number
  feedDateTo?: number
  motivations: string[]
  archetypes: string[]
  groupByBlueprint: boolean
  hideDiscontinuedBlueprints: boolean
}

const emptyArrayValue = 'no-selection'
type emptyArray = typeof emptyArrayValue

export const liveEventsTrackingSearchParamsMapper: UrlSearchParamsMapper<SearchParamSource, SearchParamDestination> = {
  mapFromUrl: ({ groupByArchetype, hideDiscontinuedArchetypes, ...source }: SearchParamSource): SearchParamDestination => {
    const groupByBlueprint = source.groupByBlueprint ?? groupByArchetype
    const hideDiscontinuedBlueprints = source.hideDiscontinuedBlueprints ?? hideDiscontinuedArchetypes
    return {
      ...source,
      gameIds: Array.isArray(source.gameIds) ? source.gameIds : source.gameIds ? [source.gameIds] : [],
      liveEventTags: source.liveEventTags || [],
      liveEventSecondaryTags: source.liveEventSecondaryTags || [],
      liveEventDurations: source.liveEventDurations || [],
      liveEventAppearances: source.liveEventAppearances || [],
      liveEventPricePoints: source.liveEventPricePoints || [],
      additionalDatas:
        source.additionalDatas === emptyArrayValue ? [] : !source.additionalDatas ? Object.values(LiveEventCalendarAdditionalDataId) : source.additionalDatas,
      sortGamesBy: (source.sortGamesBy as SortGamesByOptionValue) || SortGamesByOptionValue.SustainedGrossingRank,
      dateFrom: isNumeric(source.dateFrom) ? +(source.dateFrom as string) : undefined,
      dateTo: isNumeric(source.dateTo) ? +(source.dateTo as string) : undefined,
      performanceEffectThreshold: +source.performanceEffectThreshold || defaultPerformanceEffectThresholdValue,
      performanceChangesBy: (source.performanceChangesBy || defaultPerformanceChangesByValue) as PerformanceChangesByOptionValue,
      feedDateFrom: isNumeric(source.feedDateFrom) ? +(source.feedDateFrom as string) : undefined,
      feedDateTo: isNumeric(source.feedDateTo) ? +(source.feedDateTo as string) : undefined,
      motivations: source.motivations || [],
      archetypes: source.archetypes || [],
      groupByBlueprint: groupByBlueprint === 'true',
      hideDiscontinuedBlueprints: hideDiscontinuedBlueprints === undefined ? true : hideDiscontinuedBlueprints === 'true',
    }
  },
  mapToUrl: (destination: SearchParamDestination): SearchParamSource => {
    return {
      ...destination,
      gameIds: destination.gameIds || [],
      liveEventTags: destination.liveEventTags || [],
      liveEventDurations: destination.liveEventDurations || [],
      liveEventAppearances: destination.liveEventAppearances || [],
      liveEventPricePoints: destination.liveEventPricePoints || [],
      additionalDatas: destination.additionalDatas?.length === 0 ? emptyArrayValue : destination.additionalDatas,
      sortGamesBy: destination.sortGamesBy,
      dateFrom: destination.dateFrom ? destination.dateFrom + '' : undefined,
      dateTo: destination.dateTo ? destination.dateTo + '' : undefined,
      performanceEffectThreshold: destination.performanceEffectThreshold + '',
      performanceChangesBy: destination.performanceChangesBy,
      feedDateFrom: destination.feedDateFrom ? destination.feedDateFrom + '' : undefined,
      feedDateTo: destination.feedDateTo ? destination.feedDateTo + '' : undefined,
      motivations: destination.motivations || [],
      archetypes: destination.archetypes || [],
      groupByBlueprint: destination.groupByBlueprint ? 'true' : 'false',
      hideDiscontinuedBlueprints: destination.hideDiscontinuedBlueprints ? 'true' : 'false',
    }
  },
}

export const useLiveEventsTrackingSearchParams = () => useSearchParams<SearchParamSource, SearchParamDestination>(liveEventsTrackingSearchParamsMapper)

const isNumeric = (str?: string) => !isNil(str) && !isNaN(+(str as string)) && !isNaN(parseFloat(str as string))
